import React, { useEffect, useRef, useState } from 'react';

import { getContentMetadata } from 'src/utils/journey';
import { isValidUrl } from 'src/utils/url';
import { ContentCardWithPreview, ContentCardWithPreviewProps } from '../content/card-with-preview';
import classNames from 'classnames';
import { Placement } from '@popperjs/core';
import contentEditable from 'src/utils/contentEditable';
import { apiUpdateContent } from 'src/utils/journeyApi';
import { Content } from 'src/common/interfaces/content.interface';

interface LibraryContentCardSmallProps
  extends Pick<ContentCardWithPreviewProps, 'content' | 'previewSize' | 'iconWrapperClassName'> {
  clickable?: boolean;
  editable?: boolean;
  hidePreview?: boolean;
  titleContainerClassName?: string;
  onUpdateContent?: (content: Content) => void;
}

const contentNameClasses = 'text-bedrock-p text-bedrock-black line-clamp-2 lg:line-clamp-none';

export const LibraryHoverableCardSmall = ({
  content,
  onUpdateContent,
  titleContainerClassName,
  iconWrapperClassName = 'rounded-md',
  clickable,
  previewSize,
  editable,
  hidePreview = false,
}: LibraryContentCardSmallProps) => {
  const contentMetadata = getContentMetadata(content);
  const isMetaURL = Boolean(isValidUrl(contentMetadata));
  const [placement, setPlacement] = useState<Placement | undefined>();
  const isPlacementResetRef = useRef(false);
  const EditableContentName = contentEditable('span');

  useEffect(() => {
    const onPageScrolled = () => {
      if (placement && !isPlacementResetRef.current) {
        isPlacementResetRef.current = true;
        setPlacement(undefined);
      }
    };

    window.addEventListener('scroll', onPageScrolled);

    return () => {
      window.removeEventListener('scroll', onPageScrolled);
    };
  }, [placement]);

  const onContentNameUpdate = (name: string) => {
    return apiUpdateContent(content.id, { name }).then((content: Content) => {
      if (onUpdateContent) {
        onUpdateContent(content);
      }
    });
  };

  const renderRow = (name: string) => (
    <div className={classNames('flex flex-col min-w-0 flex-grow-0', titleContainerClassName)}>
      {editable ? (
        <EditableContentName
          className={contentNameClasses}
          data-placeholder='Name this content'
          value={name || 'Name this content'}
          disableAutoFocus={true}
          onSave={(prevValue, newValue) => onContentNameUpdate(newValue)}
          style={{ wordBreak: 'break-word' }}
        />
      ) : (
        <span className={contentNameClasses} style={{ wordBreak: 'break-word' }}>
          {name}
        </span>
      )}
      {isMetaURL ? (
        <a
          className='text-bedrock-p has-hover text-bedrock-dark-gray break-all line-clamp-1 hover:underline'
          href={contentMetadata}
          target='_blank'
          rel='noopener noreferrer'
        >
          {contentMetadata}
        </a>
      ) : (
        <div className='text-bedrock-p text-bedrock-dark-gray truncate -mt-0.5'>{contentMetadata}</div>
      )}
    </div>
  );

  if (hidePreview) {
    return renderRow(content.name);
  }

  return (
    <ContentCardWithPreview
      key={`content-card-with-preview-${placement}-${content.name}`}
      content={content}
      iconWrapperClassName={iconWrapperClassName}
      previewSize={previewSize}
      placement={placement}
      containerClassName={classNames('justify-start items-start rounded-lg', {
        'cursor-default': !clickable,
        'cursor-pointer': clickable,
      })}
      onPlacementChange={(placement) => {
        isPlacementResetRef.current = false;
        setPlacement(placement);
      }}
    >
      {(name) => renderRow(name)}
    </ContentCardWithPreview>
  );
};
